/**
 * §B-8 — minimal CSV parser for the barcode/bin bulk import. Handles a header
 * row, quoted fields, commas-in-quotes, and CRLF. Pure → unit-tested. Not a full
 * RFC-4180 implementation (no embedded newlines in quotes) — enough for the
 * paste-a-spreadsheet import flow.
 */
export type CsvRow = Record<string, string>;

/** Split one CSV line into fields, honouring double-quotes + "" escapes. */
function splitLine(line: string): string[] {
  const out: string[] = [];
  let cur = "";
  let inQ = false;
  for (let i = 0; i < line.length; i++) {
    const c = line[i];
    if (inQ) {
      if (c === '"') {
        if (line[i + 1] === '"') {
          cur += '"';
          i++;
        } else inQ = false;
      } else cur += c;
    } else if (c === '"') {
      inQ = true;
    } else if (c === ",") {
      out.push(cur);
      cur = "";
    } else cur += c;
  }
  out.push(cur);
  return out.map((s) => s.trim());
}

/**
 * Parse CSV text to objects keyed by the (lower-cased) header row. Blank lines
 * are skipped. Returns [] for empty/whitespace input.
 */
export function parseCsv(text: string): CsvRow[] {
  const lines = text.split(/\r?\n/).filter((l) => l.trim() !== "");
  if (lines.length < 2) return [];
  const headers = splitLine(lines[0]!).map((h) => h.toLowerCase());
  const rows: CsvRow[] = [];
  for (let i = 1; i < lines.length; i++) {
    const cells = splitLine(lines[i]!);
    const row: CsvRow = {};
    headers.forEach((h, j) => {
      row[h] = cells[j] ?? "";
    });
    rows.push(row);
  }
  return rows;
}

/** Coerce common truthy strings to boolean (for an isPrimary CSV column). */
export function csvBool(v: string | undefined): boolean {
  return /^(1|true|yes|y|primary)$/i.test((v ?? "").trim());
}

/** Quote a single CSV field when it contains a comma, quote, or newline (RFC-4180). */
function csvField(value: string): string {
  return /[",\r\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
}

/**
 * Serialize a header row + body rows to CSV text (Task 3 export). Pure → tested.
 * Fields are escaped per RFC-4180; rows are CRLF-joined for spreadsheet parity.
 */
export function serializeCsv(headers: string[], rows: string[][]): string {
  return [headers, ...rows].map((r) => r.map(csvField).join(",")).join("\r\n");
}

/** Trigger a client-side download of CSV text (DOM-only; no-op outside a browser). */
export function downloadCsv(filename: string, csv: string): void {
  if (typeof document === "undefined") return;
  // Prepend a BOM so Excel reads UTF-8 (accented customer names) correctly.
  const blob = new Blob(["﻿", csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
